import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, User, Search, X, ChevronUp, ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import TagInput from "@/components/TagInput";
import UploadExcelButton from "@/components/UploadExcelButton";
import { Link } from "react-router-dom";
import { FbmStatCellGraded } from "@/components/FbmStat";

const emptyForm = {
  full_name: "",
  date_of_birth: "",
  birth_nationality: "",
  passport_nationalities: [],
  preferred_foot: "right",
  position: "",
  national_team_status: "",
  national_team_level: "",
  height_cm: "",
  weight_kg: "",
  team_ids: [],
};

// Phase 12.2 — standard football position abbreviations.
// Phase 12.3: trimmed to 10 (removed LM, RM, ST, Other / free-text input).
const POSITION_OPTIONS = [
  { value: "GK", label: "GK · Goalkeeper" },
  { value: "RB", label: "RB · Right Back" },
  { value: "LB", label: "LB · Left Back" },
  { value: "CB", label: "CB · Center Back" },
  { value: "DM", label: "DM · Defensive Midfielder" },
  { value: "CM", label: "CM · Central Midfielder" },
  { value: "AM", label: "AM · Attacking Midfielder" },
  { value: "LW", label: "LW · Left Winger" },
  { value: "RW", label: "RW · Right Winger" },
  { value: "CF", label: "CF · Center Forward" },
];
const POSITION_CODES = POSITION_OPTIONS.map((p) => p.value);
const EMPTY_SENTINEL = "__none__";

function PositionField({ value, onChange, testIdPrefix }) {
  // Drives a single string state. If the stored value isn't in the standard
  // list (e.g. legacy "ST" / "LM" / "RM" or a free-text from the previous
  // build) we still SHOW it in the trigger via SelectValue but the dropdown
  // doesn't surface it as a selectable option — user must pick one of the
  // 10 standard codes or "— none —" to change it.
  const isStandard = !!value && POSITION_CODES.includes(value);
  const selectValue = !value ? EMPTY_SENTINEL : (isStandard ? value : value);

  const handleSelect = (v) => {
    if (v === EMPTY_SENTINEL) onChange("");
    else onChange(v);
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-wrapper`}>
      <Label>Position <span className="text-muted-foreground font-normal">(optional)</span></Label>
      <Select value={selectValue} onValueChange={handleSelect}>
        <SelectTrigger data-testid={`${testIdPrefix}-select`}>
          <SelectValue placeholder="Pick a position…">
            {isStandard
              ? POSITION_OPTIONS.find((o) => o.value === value)?.label
              : (value || null)}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={EMPTY_SENTINEL} data-testid={`${testIdPrefix}-option-none`}>— none —</SelectItem>
          {POSITION_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value} data-testid={`${testIdPrefix}-option-${o.value.toLowerCase()}`}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// Phase 14 — national-team paired-required pair of selects.
const NATIONAL_TEAM_STATUS_OPTIONS = ["Current", "Former"];
const NATIONAL_TEAM_LEVEL_OPTIONS = [
  "U15", "U16", "U17", "U18", "U19", "U20", "U21", "National Team",
];
const NT_EMPTY = "__none__";

function NationalTeamFields({ status, level, onStatusChange, onLevelChange, testIdPrefix }) {
  const mismatch = (!!status) !== (!!level);
  return (
    <>
      <div className="grid grid-cols-2 gap-4" data-testid={`${testIdPrefix}-wrapper`}>
        <div className="space-y-2">
          <Label>National team status <span className="text-muted-foreground font-normal">(optional)</span></Label>
          <Select
            value={status || NT_EMPTY}
            onValueChange={(v) => onStatusChange(v === NT_EMPTY ? "" : v)}
          >
            <SelectTrigger data-testid={`${testIdPrefix}-status-select`}>
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NT_EMPTY}>— none —</SelectItem>
              {NATIONAL_TEAM_STATUS_OPTIONS.map((o) => (
                <SelectItem key={o} value={o} data-testid={`${testIdPrefix}-status-${o.toLowerCase()}`}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>National team level <span className="text-muted-foreground font-normal">(optional)</span></Label>
          <Select
            value={level || NT_EMPTY}
            onValueChange={(v) => onLevelChange(v === NT_EMPTY ? "" : v)}
          >
            <SelectTrigger data-testid={`${testIdPrefix}-level-select`}>
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NT_EMPTY}>— none —</SelectItem>
              {NATIONAL_TEAM_LEVEL_OPTIONS.map((o) => (
                <SelectItem key={o} value={o} data-testid={`${testIdPrefix}-level-${o.replace(/\s+/g,'-').toLowerCase()}`}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {mismatch && (
        <p className="text-xs text-rose-500 -mt-2" data-testid={`${testIdPrefix}-error`}>
          Both fields must be set together.
        </p>
      )}
    </>
  );
}

// Two-step team picker subcomponent
function TwoStepTeamPicker({ teams, leagues, value, onChange, testIdPrefix }) {
  const [leagueId, setLeagueId] = useState("");
  const [teamId, setTeamId] = useState("");
  const teamsForLeague = teams.filter((t) => leagueId && (t.league_ids || []).includes(leagueId) && !value.includes(t.id));
  const teamsById = Object.fromEntries(teams.map((t) => [t.id, t]));

  const addTeam = () => {
    if (!teamId || value.includes(teamId)) return;
    onChange([...value, teamId]);
    setTeamId("");
  };
  const removeTeam = (id) => onChange(value.filter((x) => x !== id));

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-root`}>
      <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">League</Label>
          <Select value={leagueId} onValueChange={(v) => { setLeagueId(v); setTeamId(""); }}>
            <SelectTrigger data-testid={`${testIdPrefix}-league-select`}>
              <SelectValue placeholder="Pick a league" />
            </SelectTrigger>
            <SelectContent>
              {leagues.length === 0 ? (
                <div className="px-3 py-4 text-sm text-muted-foreground">No leagues</div>
              ) : leagues.map((l) => (
                <SelectItem key={l.id} value={l.id} data-testid={`${testIdPrefix}-league-option-${l.id}`}>{l.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Team</Label>
          <Select value={teamId} onValueChange={setTeamId} disabled={!leagueId}>
            <SelectTrigger data-testid={`${testIdPrefix}-team-select`}>
              <SelectValue placeholder={leagueId ? (teamsForLeague.length ? "Pick a team" : "No more teams") : "Pick league first"} />
            </SelectTrigger>
            <SelectContent>
              {teamsForLeague.map((t) => (
                <SelectItem key={t.id} value={t.id} data-testid={`${testIdPrefix}-team-option-${t.id}`}>{t.name} ({t.country})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button type="button" onClick={addTeam} disabled={!teamId} data-testid={`${testIdPrefix}-add-button`} className="h-10">
          Add
        </Button>
      </div>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-chips`}>
          {value.map((id) => {
            const t = teamsById[id];
            return (
              <Badge key={id} variant="secondary" className="rounded-none gap-1 pr-1" data-testid={`${testIdPrefix}-chip-${id}`}>
                {t ? `${t.name} (${t.country})` : id}
                <button type="button" onClick={() => removeTeam(id)} className="inline-flex items-center justify-center rounded-sm hover:bg-muted-foreground/20 p-0.5" aria-label="Remove">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
}

const FOOT_OPTIONS = [
  { value: "left", label: "Left" },
  { value: "right", label: "Right" },
  { value: "both", label: "Both" },
];

export default function PlayersPage() {
  const { user } = useAuth();
  const ignoreFriendlies = user?.ignore_friendlies ?? true;
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [q, setQ] = useState("");
  const [filterLeagueId, setFilterLeagueId] = useState("");
  const [filterTeamId, setFilterTeamId] = useState("");
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ ...emptyForm, team_ids: [] });
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [fbm, setFbm] = useState({});

  const load = async (query = "", teamId = "") => {
    setLoading(true);
    try {
      const params = {};
      if (query) params.q = query;
      if (teamId) params.team_id = teamId;
      const [p, t, l] = await Promise.all([
        api.get("/players", { params }),
        api.get("/teams"),
        api.get("/leagues"),
      ]);
      setPlayers(p.data); setTeams(t.data); setLeagues(l.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(() => load(q, filterTeamId), 250);
    return () => clearTimeout(t);
  }, [q, filterTeamId]);

  // League-only filter narrows the player list client-side via team_ids → league_ids
  const teamsInLeague = filterLeagueId
    ? teams.filter((t) => (t.league_ids || []).includes(filterLeagueId))
    : teams;

  const teamIdsInLeague = new Set(teamsInLeague.map((t) => t.id));

  const visiblePlayers = filterLeagueId && !filterTeamId
    ? players.filter((p) => (p.team_ids || []).some((tid) => teamIdsInLeague.has(tid)))
    : players;

  // Fetch FBM bulk (Phase 11.9 — adaptive mode) for visible players whenever
  // the visible set or the ignore_friendlies preference changes.
  useEffect(() => {
    const ids = visiblePlayers.map((p) => p.id);
    if (!ids.length) { setFbm({}); return; }
    const csv = ids.join(",");
    let alive = true;
    api.get(`/fbm/bulk`, { params: { ids: csv, mode: "adaptive" } })
      .then((r) => { if (alive) setFbm(r.data || {}); })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visiblePlayers.map((p) => p.id).join(","), filterLeagueId, ignoreFriendlies]);

  // Phase 11.9 — client-side sort state, default FBM desc.
  const [sortBy, setSortBy] = useState("fbm_score");
  const [sortDir, setSortDir] = useState("desc");

  // Phase 17 — default-view filter: when no search/selector is active,
  // admin sees only players still missing uploaded match data (the upload
  // to-do list). Any filter activation reverts to showing all matches.
  const filtersActive = Boolean(q || filterLeagueId || filterTeamId);

  const sortedPlayers = useMemo(() => {
    let rows = visiblePlayers;
    if (!filtersActive) {
      rows = rows.filter((p) => p.has_match_data === false);
    }
    rows = rows.map((p) => {
      const f = fbm[p.id] || {};
      return {
        ...p,
        _fbm: f,
        fbm_score: f.fbm_score ?? null,
        overall: f.overall ?? null,
        finishing: f.finishing ?? null,
        passing: f.passing ?? null,
        defending: f.defending ?? null,
        minutes_played: f.minutes_played ?? null,
      };
    });
    const getVal = (r) => sortBy === "full_name" ? (r.full_name || "") : r[sortBy];
    rows.sort((a, b) => {
      const av = getVal(a); const bv = getVal(b);
      const aNull = av == null || av === ""; const bNull = bv == null || bv === "";
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      if (sortBy === "full_name") {
        return sortDir === "asc"
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      }
      return sortDir === "asc" ? Number(av) - Number(bv) : Number(bv) - Number(av);
    });
    return rows;
  }, [visiblePlayers, fbm, sortBy, sortDir, filtersActive]);

  const onSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(key); setSortDir(key === "full_name" ? "asc" : "desc"); }
  };

  const openCreate = () => { setCreateForm(emptyForm); setCreateOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setEditForm({
      full_name: p.full_name,
      date_of_birth: p.date_of_birth || "",
      birth_nationality: p.birth_nationality,
      passport_nationalities: p.passport_nationalities || [],
      preferred_foot: p.preferred_foot,
      position: p.position || "",
      national_team_status: p.national_team_status || "",
      national_team_level: p.national_team_level || "",
      height_cm: p.height_cm == null ? "" : String(p.height_cm),
      weight_kg: p.weight_kg == null ? "" : String(p.weight_kg),
      team_ids: p.team_ids || [],
    });
  };

  // Phase 14 — paired-required validation helper. Returns "" when OK or an
  // error message when the pair is half-set.
  const validateNationalTeam = (status, level) => {
    if ((!!status) !== (!!level)) {
      return "Both fields must be set together.";
    }
    return "";
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const ntErr = validateNationalTeam(createForm.national_team_status, createForm.national_team_level);
    if (ntErr) { toast.error(ntErr); return; }
    setSaving(true);
    try {
      await api.post("/players", {
        full_name: createForm.full_name.trim(),
        date_of_birth: createForm.date_of_birth || null,
        birth_nationality: createForm.birth_nationality.trim(),
        passport_nationalities: createForm.passport_nationalities,
        preferred_foot: createForm.preferred_foot,
        position: createForm.position.trim() || null,
        national_team_status: createForm.national_team_status || null,
        national_team_level: createForm.national_team_level || null,
        height_cm: createForm.height_cm === "" ? null : parseInt(createForm.height_cm, 10),
        weight_kg: createForm.weight_kg === "" ? null : parseInt(createForm.weight_kg, 10),
        team_ids: createForm.team_ids,
      });
      toast.success("Player created"); setCreateOpen(false); await load(q);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    const ntErr = validateNationalTeam(editForm.national_team_status, editForm.national_team_level);
    if (ntErr) { toast.error(ntErr); return; }
    setSaving(true);
    try {
      await api.patch(`/players/${editing.id}`, {
        full_name: editForm.full_name.trim(),
        date_of_birth: editForm.date_of_birth || null,
        birth_nationality: editForm.birth_nationality.trim(),
        passport_nationalities: editForm.passport_nationalities,
        preferred_foot: editForm.preferred_foot,
        position: editForm.position.trim() || null,
        national_team_status: editForm.national_team_status || null,
        national_team_level: editForm.national_team_level || null,
        height_cm: editForm.height_cm === "" ? null : parseInt(editForm.height_cm, 10),
        weight_kg: editForm.weight_kg === "" ? null : parseInt(editForm.weight_kg, 10),
        team_ids: editForm.team_ids,
      });
      toast.success("Player updated"); setEditing(null); await load(q);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/players/${toDelete.id}`);
      toast.success("Player deleted"); setToDelete(null); await load(q);
    } catch (err) { toast.error(formatApiError(err)); setToDelete(null); }
  };

  return (
    <div data-testid="players-page">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">Roster</div>
          <h1 className="font-display uppercase text-4xl md:text-5xl font-bold tracking-tight">Players</h1>
          <p className="text-muted-foreground mt-2 max-w-lg">Player profiles with live FBM behavioural probabilities.</p>
        </div>
        <Button onClick={openCreate} className="h-11 rounded-none font-display uppercase tracking-[0.18em]" data-testid="create-player-button">
          <Plus className="h-4 w-4 mr-2" /> New Player
        </Button>
      </header>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="relative max-w-sm flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search players…" className="pl-9" data-testid="players-search-input" />
        </div>
        <div className="space-y-1 w-[200px]">
          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">League</Label>
          <Select value={filterLeagueId || "__all__"} onValueChange={(v) => { setFilterLeagueId(v === "__all__" ? "" : v); setFilterTeamId(""); }}>
            <SelectTrigger data-testid="players-filter-league-select">
              <SelectValue placeholder="All leagues" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__" data-testid="players-filter-league-option-all">All leagues</SelectItem>
              {leagues.map((l) => (
                <SelectItem key={l.id} value={l.id} data-testid={`players-filter-league-option-${l.id}`}>{l.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 w-[220px]">
          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Team</Label>
          <Select value={filterTeamId || "__all__"} onValueChange={(v) => setFilterTeamId(v === "__all__" ? "" : v)} disabled={!filterLeagueId && teamsInLeague.length === 0}>
            <SelectTrigger data-testid="players-filter-team-select">
              <SelectValue placeholder={filterLeagueId ? "All teams in league" : "All teams"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__" data-testid="players-filter-team-option-all">{filterLeagueId ? "All teams in league" : "All teams"}</SelectItem>
              {teamsInLeague.map((t) => (
                <SelectItem key={t.id} value={t.id} data-testid={`players-filter-team-option-${t.id}`}>{t.name} ({t.country})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {(filterLeagueId || filterTeamId || q) && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => { setQ(""); setFilterLeagueId(""); setFilterTeamId(""); }}
            data-testid="players-filter-clear"
            className="h-10"
          >
            Clear filters
          </Button>
        )}
      </div>

      {/* Phase 17 — view-mode caption */}
      <div
        className="text-xs text-muted-foreground mb-3"
        data-testid="players-view-caption"
      >
        {filtersActive ? (
          <span data-testid="players-view-caption-filtered">
            Showing {sortedPlayers.length} matching player{sortedPlayers.length === 1 ? "" : "s"}
          </span>
        ) : (
          <span data-testid="players-view-caption-default">
            Showing players without uploaded data · {sortedPlayers.length} player{sortedPlayers.length === 1 ? "" : "s"}{" "}
            <span className="text-muted-foreground/70">— type or use filters to see all players</span>
          </span>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card overflow-x-auto shadow-sm">
        <Table data-testid="players-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent border-border">
              <PlayersTh label="Name"      sortKey="full_name"      sortBy={sortBy} sortDir={sortDir} onClick={onSort} align="left"  testId="players-sort-name" />
              <TableHead className="px-4 py-4 font-display uppercase tracking-[0.15em] text-[11px]">Team(s)</TableHead>
              <PlayersTh label="FBM"       sortKey="fbm_score"      sortBy={sortBy} sortDir={sortDir} onClick={onSort} testId="players-sort-fbm" />
              <PlayersTh label="Ovr"       sortKey="overall"        sortBy={sortBy} sortDir={sortDir} onClick={onSort} testId="players-sort-overall" />
              <PlayersTh label="Fin"       sortKey="finishing"      sortBy={sortBy} sortDir={sortDir} onClick={onSort} testId="players-sort-finishing" />
              <PlayersTh label="Pas"       sortKey="passing"        sortBy={sortBy} sortDir={sortDir} onClick={onSort} testId="players-sort-passing" />
              <PlayersTh label="Def"       sortKey="defending"      sortBy={sortBy} sortDir={sortDir} onClick={onSort} testId="players-sort-defending" />
              <PlayersTh label="Min"       sortKey="minutes_played" sortBy={sortBy} sortDir={sortDir} onClick={onSort} testId="players-sort-minutes" />
              <TableHead className="px-4 py-4 font-display uppercase tracking-[0.15em] text-[11px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="px-6 py-12 text-center text-muted-foreground">Loading…</TableCell></TableRow>
            ) : sortedPlayers.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="px-6 py-16 text-center">
                <User className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
                <div className="font-display uppercase tracking-wider">No players match</div>
              </TableCell></TableRow>
            ) : (
              sortedPlayers.map((p) => {
                const f = p._fbm || {};
                return (
                  <TableRow key={p.id} className="tr-fade border-border hover:bg-muted/30" data-testid={`player-row-${p.id}`}>
                    <TableCell className="px-4 py-3 font-medium">
                      <div className="flex items-center gap-2">
                        <Link to={`/admin/players/${p.id}`} className="hover:underline" data-testid={`player-link-${p.id}`}>
                          {p.full_name}
                        </Link>
                        {f.period_label && (
                          <span
                            data-testid={`player-period-${p.id}`}
                            className={`text-[10px] uppercase tracking-[0.15em] rounded px-1.5 py-0.5 border ${
                              f.period_kind === "season+prev"
                                ? "border-amber-500/40 text-amber-500/80"
                                : "border-border text-muted-foreground"
                            }`}
                            title={
                              f.period_kind === "season+prev"
                                ? `Newest season has only ${f.match_count} match${f.match_count === 1 ? "" : "es"} in window — folded in previous season.`
                                : `Isolation on ${f.period_label} (${f.match_count ?? 0} matches).`
                            }
                          >
                            {f.period_label.replace(/(\d{2})(\d{2})\/(\d{2})/g, "$2/$3")}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(p.teams || []).length === 0 ? (
                          <span className="text-muted-foreground text-xs">—</span>
                        ) : p.teams.map((t) => (
                          <Badge key={t.id} variant="secondary" className="rounded-none uppercase tracking-wider text-[10px]">{t.name}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="px-2 py-3 text-right font-mono font-semibold text-foreground/85" data-testid={`fbm-score-${p.id}`}>
                      {f.fbm_score == null ? <span className="text-muted-foreground">—</span> : (Math.ceil(Number(f.fbm_score) * 10) / 10).toFixed(1)}
                    </TableCell>
                    <TableCell className="px-2 py-3 text-right"><FbmStatCellGraded value={f.overall}   testId={`fbm-overall-${p.id}`} /></TableCell>
                    <TableCell className="px-2 py-3 text-right"><FbmStatCellGraded value={f.finishing} testId={`fbm-finishing-${p.id}`} /></TableCell>
                    <TableCell className="px-2 py-3 text-right"><FbmStatCellGraded value={f.passing}   testId={`fbm-passing-${p.id}`} /></TableCell>
                    <TableCell className="px-2 py-3 text-right"><FbmStatCellGraded value={f.defending} testId={`fbm-defending-${p.id}`} /></TableCell>
                    <TableCell className="px-2 py-3 text-right font-mono text-muted-foreground" data-testid={`fbm-minutes-${p.id}`}>{f.minutes_played ?? "—"}</TableCell>
                    <TableCell className="px-4 py-3 text-right whitespace-nowrap">
                      <UploadExcelButton url={`/players/${p.id}/import-excel`} testId={`upload-player-${p.id}`} onResult={() => load(q, filterTeamId)} />
                      <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-player-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setToDelete(p)} data-testid={`delete-player-${p.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-player-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl">New Player</DialogTitle>
            <DialogDescription>Profile + optional team assignment. Teams can also be added or changed later from Edit.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="p-name">Full name</Label>
              <Input id="p-name" value={createForm.full_name} onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })} required data-testid="player-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="p-dob">Date of birth <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input id="p-dob" type="date" value={createForm.date_of_birth} onChange={(e) => setCreateForm({ ...createForm, date_of_birth: e.target.value })} data-testid="player-dob-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-bn">Birth nationality</Label>
                <Input id="p-bn" value={createForm.birth_nationality} onChange={(e) => setCreateForm({ ...createForm, birth_nationality: e.target.value })} required data-testid="player-birth-nat-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Passport nationalities <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <TagInput
                value={createForm.passport_nationalities}
                onChange={(v) => setCreateForm({ ...createForm, passport_nationalities: v })}
                placeholder="Type and press Enter (e.g. Dutch, Surinamese)"
                testIdPrefix="player-passports"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Preferred foot</Label>
                <Select value={createForm.preferred_foot} onValueChange={(v) => setCreateForm({ ...createForm, preferred_foot: v })}>
                  <SelectTrigger data-testid="player-foot-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{FOOT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`player-foot-${o.value}`}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <PositionField
                value={createForm.position}
                onChange={(v) => setCreateForm({ ...createForm, position: v })}
                testIdPrefix="player-position"
              />
            </div>
            <NationalTeamFields
              status={createForm.national_team_status}
              level={createForm.national_team_level}
              onStatusChange={(v) => setCreateForm({ ...createForm, national_team_status: v })}
              onLevelChange={(v) => setCreateForm({ ...createForm, national_team_level: v })}
              testIdPrefix="player-nt"
            />
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="p-h">Height (cm) <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input id="p-h" type="number" min="1" value={createForm.height_cm} onChange={(e) => setCreateForm({ ...createForm, height_cm: e.target.value })} data-testid="player-height-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-w">Weight (kg) <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input id="p-w" type="number" min="1" value={createForm.weight_kg} onChange={(e) => setCreateForm({ ...createForm, weight_kg: e.target.value })} data-testid="player-weight-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Teams <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <TwoStepTeamPicker
                teams={teams}
                leagues={leagues}
                value={createForm.team_ids}
                onChange={(v) => setCreateForm({ ...createForm, team_ids: v })}
                testIdPrefix="player-teams"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="player-create-submit">{saving ? "Creating…" : "Create player"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-player-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl">Edit Player</DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={handleEdit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="e-name">Full name</Label>
                <Input id="e-name" value={editForm.full_name} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} required data-testid="edit-player-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="e-dob">Date of birth <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <Input id="e-dob" type="date" value={editForm.date_of_birth} onChange={(e) => setEditForm({ ...editForm, date_of_birth: e.target.value })} data-testid="edit-player-dob-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="e-bn">Birth nationality</Label>
                  <Input id="e-bn" value={editForm.birth_nationality} onChange={(e) => setEditForm({ ...editForm, birth_nationality: e.target.value })} required data-testid="edit-player-birth-nat-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Passport nationalities <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <TagInput
                  value={editForm.passport_nationalities}
                  onChange={(v) => setEditForm({ ...editForm, passport_nationalities: v })}
                  placeholder="Type and press Enter"
                  testIdPrefix="edit-player-passports"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Preferred foot</Label>
                  <Select value={editForm.preferred_foot} onValueChange={(v) => setEditForm({ ...editForm, preferred_foot: v })}>
                    <SelectTrigger data-testid="edit-player-foot-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{FOOT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <PositionField
                  value={editForm.position}
                  onChange={(v) => setEditForm({ ...editForm, position: v })}
                  testIdPrefix="edit-player-position"
                />
              </div>
              <NationalTeamFields
                status={editForm.national_team_status}
                level={editForm.national_team_level}
                onStatusChange={(v) => setEditForm({ ...editForm, national_team_status: v })}
                onLevelChange={(v) => setEditForm({ ...editForm, national_team_level: v })}
                testIdPrefix="edit-player-nt"
              />
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="e-h">Height (cm) <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <Input id="e-h" type="number" min="1" value={editForm.height_cm} onChange={(e) => setEditForm({ ...editForm, height_cm: e.target.value })} data-testid="edit-player-height-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="e-w">Weight (kg) <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <Input id="e-w" type="number" min="1" value={editForm.weight_kg} onChange={(e) => setEditForm({ ...editForm, weight_kg: e.target.value })} data-testid="edit-player-weight-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Teams</Label>
                <TwoStepTeamPicker
                  teams={teams}
                  leagues={leagues}
                  value={editForm.team_ids}
                  onChange={(v) => setEditForm({ ...editForm, team_ids: v })}
                  testIdPrefix="edit-player-teams"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button type="submit" disabled={saving} data-testid="edit-player-submit">{saving ? "Saving…" : "Save changes"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete player?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete <strong>{toDelete?.full_name}</strong>.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-player-confirm-button">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** Sortable header (Phase 11.9 — players roster). */
function PlayersTh({ label, sortKey, sortBy, sortDir, onClick, align = "right", testId }) {
  const active = sortBy === sortKey;
  const padding = align === "right" ? "px-2" : "px-4";
  return (
    <TableHead
      className={`${padding} py-4 font-display uppercase tracking-[0.15em] text-[11px] cursor-pointer select-none ${align === "right" ? "text-right" : ""}`}
      onClick={() => onClick(sortKey)}
      data-testid={testId}
    >
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "justify-end w-full" : ""}`}>
        {label}
        {active
          ? (sortDir === "asc"
              ? <ChevronUp className="h-3.5 w-3.5" data-testid={`${testId}-asc`} />
              : <ChevronDown className="h-3.5 w-3.5" data-testid={`${testId}-desc`} />)
          : <ChevronDown className="h-3.5 w-3.5 opacity-25" />}
      </span>
    </TableHead>
  );
}
